/**
 * Edit on top of a CV template like Word.
 * Click text to type; toolbar colors/bold apply to the selection.
 * data-field spans sync with the form via onContentChange / patchFromContent.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../contexts/LanguageContext';
import {
  patchEditorFields,
  annotateSyncFields,
} from '../utils/cvFieldSync';
import './OnTemplateEditor.css';

export const DEMO_CV_HTML = `
<style>
  .demo-cv { width: 100%; min-height: 297mm; box-sizing: border-box; padding: 14mm 16mm; font-family: "Source Sans 3", system-ui, sans-serif; color: #1c1917; background: #fff; }
  .demo-cv h1 { font-family: "Playfair Display", Georgia, serif; font-size: 28px; margin: 0 0 4px; font-weight: 600; }
  .demo-cv .demo-title { color: #1e3a5f; font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 700; margin-bottom: 10px; }
  .demo-cv .demo-contact { font-size: 11px; color: #57534e; margin-bottom: 16px; }
  .demo-cv h2 { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: #1e3a5f; border-bottom: 1px solid #1e3a5f; padding-bottom: 4px; margin: 16px 0 8px; }
  .demo-cv p, .demo-cv li { font-size: 12px; line-height: 1.45; margin: 0 0 6px; }
  .demo-cv .demo-job { font-weight: 700; font-size: 13px; }
  .demo-cv .demo-meta { color: #57534e; font-size: 11px; margin-bottom: 4px; }
</style>
<div class="demo-cv">
  <h1><span data-field="name">Alex Martín Ruiz</span></h1>
  <div class="demo-title"><span data-field="title">Product Manager</span></div>
  <div class="demo-contact">
    <span data-field="location">Madrid</span> ·
    <span data-field="phone">600 000 000</span> ·
    <span data-field="email">alex@email.com</span>
  </div>
  <h2>Perfil</h2>
  <p><span data-field="summary">Product manager con 8 años en B2B SaaS. Lidero discovery, roadmap y entrega con equipos de ingeniería y diseño.</span></p>
  <h2>Experiencia</h2>
  <p class="demo-job"><span data-field="experience.0.title">Senior Product Manager</span> — <span data-field="experience.0.company">NovaTech</span></p>
  <p class="demo-meta"><span data-field="experience.0.duration">2021 — Actualidad</span> · <span data-field="experience.0.location">Madrid</span></p>
  <p><span data-field="experience.0.description">Definí la estrategia de onboarding y subí la activación un 28% en dos trimestres. Coordino un squad de 7 personas.</span></p>
  <h2>Formación</h2>
  <p class="demo-job"><span data-field="education.0.degree">Grado en Administración</span> — <span data-field="education.0.institution">Universidad Complutense</span></p>
  <p class="demo-meta"><span data-field="education.0.date">2012 — 2016</span></p>
</div>
`;

/**
 * Turn a full HTML document into style tags + body markup, without scripts.
 *
 * @param {string} fullHtml
 * @returns {string}
 */
export const prepareEditableHtml = (fullHtml) => {
  if (!fullHtml || typeof fullHtml !== 'string') {
    return DEMO_CV_HTML;
  }
  const doc = new DOMParser().parseFromString(fullHtml, 'text/html');
  doc.querySelectorAll('script').forEach((node) => node.remove());
  if (doc.body) {
    annotateSyncFields(doc.body);
  }
  const styles = Array.from(doc.querySelectorAll('style'))
    .map((node) => node.outerHTML)
    .join('');
  const body = doc.body ? doc.body.innerHTML : fullHtml;
  return `${styles}${body}`;
};

/**
 * Rebuild a full HTML document from the editable canvas for PDF/Word.
 *
 * @param {string} bodyHtml
 * @param {string} paperColor
 * @param {string} title
 * @returns {string}
 */
export const assembleExportHtml = (bodyHtml, paperColor = '#ffffff', title = 'CV') => {
  const safeBody = String(bodyHtml || '')
    .replace(/contenteditable\s*=\s*["']?true["']?/gi, '')
    .replace(/<\/script/gi, '<\\/script');
  const safeTitle = String(title || 'CV').replace(/</g, '');
  const paper = String(paperColor || '#ffffff').replace(/[^#a-zA-Z0-9(),.%\s-]/g, '');
  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${safeTitle}</title>
<style>
html, body { margin: 0; padding: 0; background: ${paper}; }
.cv-container, .page, .sheet, .demo-cv { background: ${paper} !important; }
</style>
</head>
<body>
${safeBody}
</body>
</html>`;
};

const FONT_SIZES = ['2', '3', '4', '5'];

/**
 * @param {object} props
 * @param {React.Ref} ref
 */
const OnTemplateEditor = forwardRef(({ html, hint, trialNote, onContentChange }, ref) => {
  const { t } = useLanguage();
  const editorRef = useRef(null);
  const savedRange = useRef(null);
  const syncTimerRef = useRef(null);
  const skipNextHtmlRef = useRef(false);
  const [textColor, setTextColor] = useState('#1c1917');
  const [paperColor, setPaperColor] = useState('#ffffff');

  const emitContent = () => {
    if (!onContentChange || !editorRef.current) {
      return;
    }
    onContentChange(editorRef.current);
  };

  const scheduleEmit = () => {
    window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(() => {
      emitContent();
    }, 120);
  };

  useImperativeHandle(ref, () => ({
    /**
     * @returns {string} Full HTML document for apply-template prefilled_html
     */
    getExportHtml: () => {
      if (!editorRef.current) {
        return '';
      }
      const plain = String(editorRef.current.innerText || '').replace(/\s+/g, ' ').trim();
      const title = plain.split(' ').slice(0, 4).join(' ') || 'CV';
      return assembleExportHtml(editorRef.current.innerHTML, paperColor, title);
    },
    /**
     * Push form values into data-field nodes without replacing the whole sheet.
     *
     * @param {object} content
     */
    patchFromContent: (content) => {
      if (!editorRef.current || !content) {
        return;
      }
      skipNextHtmlRef.current = true;
      patchEditorFields(editorRef.current, content);
    },
    /**
     * Flush pending canvas → form sync immediately.
     */
    flushSync: () => {
      window.clearTimeout(syncTimerRef.current);
      emitContent();
    },
  }));

  const rememberSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedRange.current = range;
      }
    }
  };

  const restoreSelection = () => {
    const range = savedRange.current;
    if (!range) {
      return;
    }
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const run = (command, value) => {
    restoreSelection();
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    scheduleEmit();
  };

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    if (skipNextHtmlRef.current) {
      skipNextHtmlRef.current = false;
      return;
    }
    editorRef.current.innerHTML = html;
    annotateSyncFields(editorRef.current);
    const sheet = editorRef.current.querySelector('.cv-container, .demo-cv');
    if (sheet) {
      sheet.style.background = paperColor;
    }
  }, [html]);

  useEffect(() => {
    const sheet = editorRef.current?.querySelector('.cv-container, .demo-cv');
    if (sheet) {
      sheet.style.background = paperColor;
    }
  }, [paperColor]);

  useEffect(() => () => {
    window.clearTimeout(syncTimerRef.current);
  }, []);

  return (
    <div className="on-template">
      <div className="on-template__toolbar" role="toolbar" aria-label={t('editCv.onTemplateToolbar')}>
        <button type="button" className="on-template__btn" onMouseDown={(e) => e.preventDefault()} onClick={() => run('bold')}>
          <strong>B</strong>
        </button>
        <button type="button" className="on-template__btn" onMouseDown={(e) => e.preventDefault()} onClick={() => run('italic')}>
          <em>I</em>
        </button>
        <button type="button" className="on-template__btn" onMouseDown={(e) => e.preventDefault()} onClick={() => run('underline')}>
          <span style={{ textDecoration: 'underline' }}>U</span>
        </button>
        <label className="on-template__swatch">
          <span>{t('editCv.onTemplateTextColor')}</span>
          <input
            type="color"
            value={textColor}
            onMouseDown={rememberSelection}
            onChange={(e) => {
              setTextColor(e.target.value);
              run('foreColor', e.target.value);
            }}
          />
        </label>
        <label className="on-template__swatch">
          <span>{t('editCv.onTemplateHighlight')}</span>
          <input
            type="color"
            defaultValue="#fff3bf"
            onMouseDown={rememberSelection}
            onChange={(e) => run('hiliteColor', e.target.value)}
          />
        </label>
        <label className="on-template__swatch">
          <span>{t('editCv.onTemplatePaper')}</span>
          <input
            type="color"
            value={paperColor}
            onChange={(e) => setPaperColor(e.target.value)}
          />
        </label>
        <label className="on-template__size">
          <span>{t('editCv.onTemplateSize')}</span>
          <select
            defaultValue="3"
            onMouseDown={rememberSelection}
            onChange={(e) => run('fontSize', e.target.value)}
          >
            {FONT_SIZES.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
        <button type="button" className="on-template__btn" onMouseDown={(e) => e.preventDefault()} onClick={() => run('undo')}>
          {t('editCv.onTemplateUndo')}
        </button>
      </div>
      {hint ? <p className="on-template__hint">{hint}</p> : null}
      {trialNote ? <p className="on-template__note">{trialNote}</p> : null}
      <div className="on-template__stage">
        <div
          ref={editorRef}
          className="on-template__sheet"
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          onMouseUp={rememberSelection}
          onKeyUp={rememberSelection}
          onInput={scheduleEmit}
          onBlur={emitContent}
        />
      </div>
    </div>
  );
});

OnTemplateEditor.displayName = 'OnTemplateEditor';

OnTemplateEditor.propTypes = {
  html: PropTypes.string.isRequired,
  hint: PropTypes.string,
  trialNote: PropTypes.string,
  onContentChange: PropTypes.func,
};

OnTemplateEditor.defaultProps = {
  hint: '',
  trialNote: '',
  onContentChange: null,
};

export default OnTemplateEditor;
